import { useToastr } from '@farmshare/ui-components';
import { InfoOutline } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import { useProcessorSchedulingCutsheetsPartialUpdateMutation } from 'lib/graphql';

import type { YieldItem, YieldSource } from './types';

/**
 * Narrowed shape used by the modal — accepts any object with these fields, so
 * the modal can be rendered both from the generate-invoice page (where a full
 * `SplitItem` exists) and from contexts that don't load every split field.
 */
export interface EditYieldModalSplit {
  id: string;
  cutsheetInformationId: string;
  animalHeadPublicId: string;
  tracking?: string | null;
  splitPart: string;
  yieldSources: YieldSource[];
  yieldItems: YieldItem[];
}

/**
 * Form-state version of YieldItem — number inputs are tracked as strings so the
 * user can clear a field and start typing without it snapping back to "0".
 */
interface FormYieldItem {
  capabilityOptionId?: string;
  label: string;
  weightLbs: string;
  itemCount: string;
  packageCount: string;
  packageIdentifier?: string;
}

function toFormItem(y: YieldItem): FormYieldItem {
  return {
    capabilityOptionId: y.capabilityOptionId,
    label: y.label,
    weightLbs: String(y.weightLbs),
    itemCount: String(y.itemCount),
    packageCount: String(y.packageCount),
    packageIdentifier: y.packageIdentifier,
  };
}

/**
 * Derives a starter yield list from the split's full set of cuts/extras/trims.
 * Includes everything regardless of pricing — a free cut still has yield.
 * @param sources
 */
export function deriveYieldFromSources(
  sources: YieldSource[],
): FormYieldItem[] {
  return sources.map((s) => ({
    capabilityOptionId: s.capabilityOptionId,
    label: s.label,
    weightLbs: '0',
    itemCount: '0',
    packageCount: '0',
    packageIdentifier: undefined,
  }));
}

interface EditYieldModalProps {
  open: boolean;
  onClose: () => void;
  split: EditYieldModalSplit;
  processorSchedulingId: string;
}

export function EditYieldModal({
  open,
  onClose,
  split,
  processorSchedulingId,
}: EditYieldModalProps) {
  const { push } = useToastr();
  const [items, setItems] = useState<FormYieldItem[]>([]);
  const [updateCutsheets, { loading }] =
    useProcessorSchedulingCutsheetsPartialUpdateMutation({
      refetchQueries: ['ProcessorSchedulingById'],
    });

  const pricingByOptionId = useMemo(() => {
    const map = new Map<string, NonNullable<YieldSource['pricing']>>();
    for (const s of split.yieldSources) {
      if (s.pricing && s.capabilityOptionId) {
        map.set(s.capabilityOptionId, s.pricing);
      }
    }
    return map;
  }, [split.yieldSources]);
  const pricingByLabel = useMemo(() => {
    const map = new Map<string, NonNullable<YieldSource['pricing']>>();
    for (const s of split.yieldSources) {
      if (s.pricing && !s.capabilityOptionId) {
        map.set(s.label, s.pricing);
      }
    }
    return map;
  }, [split.yieldSources]);

  const getItemPricing = (item: {
    capabilityOptionId?: string;
    label: string;
  }): NonNullable<YieldSource['pricing']> | undefined => {
    if (item.capabilityOptionId) {
      return pricingByOptionId.get(item.capabilityOptionId);
    }
    return pricingByLabel.get(item.label);
  };

  function formatPricingLabel(
    pricing: NonNullable<YieldSource['pricing']>,
  ): string {
    const price = `$${pricing.price.toFixed(2)}`;
    switch (pricing.model) {
      case 'per_hanging_weight':
        return `${price}/lb HW`;
      case 'per_pound':
        return `${price}/lb`;
      case 'per_item':
        return `${price}/ea`;
      case 'fixed_price':
        return `${price} flat`;
    }
  }

  useEffect(() => {
    if (!open) return;
    if (split.yieldItems.length === 0) {
      setItems(deriveYieldFromSources(split.yieldSources));
      return;
    }
    // Merge: keep saved rows, then append any cutsheet sources not yet covered
    // so newly-added cuts/extras/trims show up after a previous save.
    const existingByOptionId = new Set<string>();
    const existingByLabel = new Set<string>();
    for (const y of split.yieldItems) {
      if (y.capabilityOptionId) existingByOptionId.add(y.capabilityOptionId);
      else if (y.label) existingByLabel.add(y.label);
    }
    const merged: FormYieldItem[] = split.yieldItems.map(toFormItem);
    for (const src of split.yieldSources) {
      const covered = src.capabilityOptionId
        ? existingByOptionId.has(src.capabilityOptionId)
        : existingByLabel.has(src.label);
      if (covered) continue;
      merged.push({
        capabilityOptionId: src.capabilityOptionId,
        label: src.label,
        weightLbs: '0',
        itemCount: '0',
        packageCount: '0',
      });
    }
    setItems(merged);
  }, [open, split]);

  function updateItem(idx: number, patch: Partial<FormYieldItem>) {
    setItems((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    );
  }

  function removeItem(idx: number) {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }

  function addItem() {
    setItems((prev) => [
      ...prev,
      {
        label: '',
        weightLbs: '',
        itemCount: '',
        packageCount: '',
      },
    ]);
  }

  function regenerate() {
    setItems(deriveYieldFromSources(split.yieldSources));
  }

  async function handleSave() {
    if (!processorSchedulingId || !split.cutsheetInformationId) {
      push({
        title: 'Cannot save yield',
        body: 'Missing scheduling or cutsheet identifier.',
        bg: 'danger',
        delay: 5000,
      });
      return;
    }
    try {
      await updateCutsheets({
        variables: {
          processorSchedulingId,
          record: [
            {
              _id: split.cutsheetInformationId,
              yield: items.map((it) => ({
                capabilityOptionId: it.capabilityOptionId,
                label: it.label,
                weightLbs: parseFloat(it.weightLbs) || 0,
                itemCount: parseInt(it.itemCount, 10) || 0,
                packageCount: parseInt(it.packageCount, 10) || 0,
                packageIdentifier: it.packageIdentifier,
              })),
            },
          ],
        },
      });
      push({
        title: 'Yield saved',
        body: `${items.length} yield ${items.length === 1 ? 'item' : 'items'} saved.`,
        severity: 'success',
        delay: 4000,
      });
      onClose();
    } catch (error) {
      push({
        title: 'Error saving yield',
        body: (error as Error).message,
        bg: 'danger',
        delay: 5000,
      });
    }
  }

  const trackingLabel = split.tracking || split.animalHeadPublicId;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Edit Yield — {trackingLabel} ({split.splitPart})
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2" color="text.secondary">
              Track the actual output of cutting this split.
            </Typography>
            <Tooltip title="Replace the current rows with items derived from the cutsheet.">
              <Button
                size="small"
                startIcon={<AutoFixHighIcon />}
                onClick={regenerate}
              >
                Regenerate from cutsheet
              </Button>
            </Tooltip>
          </Stack>

          {items.length === 0 ? (
            <Alert severity="info">
              No yield items yet. Add one or regenerate from the cutsheet.
            </Alert>
          ) : (
            <Stack spacing={1}>
              <Stack
                direction="row"
                spacing={1}
                px={2}
                sx={{ color: 'text.secondary' }}
              >
                <Tooltip title="The name or description of the cut/extra/trim. This is used for internal tracking and does not appear on the invoice unless it's tied to a priced capability option with a blank label.">
                  <Box flex={2}>
                    <Typography variant="caption">Label</Typography>
                    <InfoOutline fontSize="small" sx={{ ml: 0.5 }} />
                  </Box>
                </Tooltip>
                <Tooltip title="The weight of the cut/extra/trim in pounds. Used for pricing when the underlying capability option is priced by weight.">
                  <Box width={120}>
                    <Typography variant="caption">Weight</Typography>
                    <InfoOutline fontSize="small" sx={{ ml: 0.5 }} />
                  </Box>
                </Tooltip>
                <Tooltip title="How many individual items were produced? E.g. how many steaks, organs, etc.?">
                  <Box width={90}>
                    <Typography variant="caption">Qty</Typography>
                    <InfoOutline fontSize="small" sx={{ ml: 0.5 }} />
                  </Box>
                </Tooltip>
                <Tooltip title="How many boxes/packages were produced?">
                  <Box width={90}>
                    <Typography variant="caption">Packs</Typography>
                    <InfoOutline fontSize="small" sx={{ ml: 0.5 }} />
                  </Box>
                </Tooltip>

                <Tooltip title="Optional identifier for when items are grouped into boxes or packages.">
                  <Box flex={1}>
                    <Typography variant="caption">Box Identifier</Typography>
                    <InfoOutline fontSize="small" sx={{ ml: 0.5 }} />
                  </Box>
                </Tooltip>

                <Box width={40} />
              </Stack>

              {items.map((item, idx) => {
                const pricing = getItemPricing(item);
                return (
                  <Stack
                    key={idx}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      borderLeft: 3,
                      borderColor: pricing ? 'success.main' : 'transparent',
                      pl: 1,
                      py: 0.25,
                      borderRadius: 0.5,
                      bgcolor: pricing ? 'success.lighter' : 'transparent',
                    }}
                  >
                    <Box flex={2}>
                      <TextField
                        size="small"
                        fullWidth
                        value={item.label}
                        onChange={(e) =>
                          updateItem(idx, { label: e.target.value })
                        }
                        InputProps={{
                          startAdornment: pricing ? (
                            <InputAdornment position="start">
                              <Tooltip title="This record produces a billed line item on the invoice.">
                                <Chip
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  icon={<AttachMoneyIcon />}
                                  label={formatPricingLabel(pricing)}
                                  sx={{ height: 22 }}
                                />
                              </Tooltip>
                            </InputAdornment>
                          ) : undefined,
                        }}
                      />
                    </Box>
                    <Box width={120}>
                      <TextField
                        size="small"
                        type="number"
                        value={item.weightLbs}
                        onChange={(e) =>
                          updateItem(idx, { weightLbs: e.target.value })
                        }
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">lbs</InputAdornment>
                          ),
                          inputProps: { step: '0.01', min: 0 },
                        }}
                      />
                    </Box>
                    <Box width={90}>
                      <TextField
                        size="small"
                        type="number"
                        value={item.itemCount}
                        onChange={(e) =>
                          updateItem(idx, { itemCount: e.target.value })
                        }
                        inputProps={{ step: '1', min: 0 }}
                      />
                    </Box>
                    <Box width={90}>
                      <TextField
                        size="small"
                        type="number"
                        value={item.packageCount}
                        onChange={(e) =>
                          updateItem(idx, { packageCount: e.target.value })
                        }
                        inputProps={{ step: '1', min: 0 }}
                      />
                    </Box>
                    <Box flex={1}>
                      <TextField
                        size="small"
                        fullWidth
                        value={item.packageIdentifier ?? ''}
                        onChange={(e) =>
                          updateItem(idx, {
                            packageIdentifier: e.target.value || undefined,
                          })
                        }
                      />
                    </Box>
                    <Box width={40}>
                      <IconButton
                        size="small"
                        onClick={() => removeItem(idx)}
                        aria-label="Remove yield item"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Stack>
                );
              })}
            </Stack>
          )}

          <Box>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addItem}
              fullWidth
            >
              Add row
            </Button>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={loading}>
          {loading ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
